import React from 'react';
import { useAppDispatch, useAppSelector, useAuth } from 'answers-core/src/redux/hooks/app-hooks';

import { useGetUserQuery } from '../../api/endpoints/users-endpoint';
import { getValidTabNameValue } from '../../redux/helpers';
import { useIsUnleashReady } from '../../redux/hooks/unleash-hooks';
import { setDefaultTabInitialized } from '../../redux/slices/app/app-slice';
import { handleTabSwitch } from '../../redux/thunks/tabs-thunk';
import { getEventBus } from '../../services/core/event-bus/event-bus';
import { isPopupMode, isPortalWebMode } from '../../utils/extension-mode';
import { PreloadAnimation } from '../Animations/Preload/PreloadAnimation';

const fullScreenLoader = isPortalWebMode() || isPopupMode();

export interface IInitializeUserDefaultSettingsProps {
    children: React.ReactElement;
    enableLoader?: boolean;
}

/**
 * Wrap with this component to initialize user default tab settings
 *
 * @param children
 * @param enableLoader
 * @constructor
 */
export const InitializeUserDefaultSettings: React.FC<IInitializeUserDefaultSettingsProps> = ({
    children,
    enableLoader = true,
}) => {
    const dispatch = useAppDispatch();
    const { isUsersFlagsReady } = useIsUnleashReady();
    const sidebarVisible = useAppSelector((state) => state.app.sidebarVisible);
    const defaultTabInitialized = useAppSelector((state) => state.app.defaultTabInitialized);
    const [isAuthorized] = useAuth();
    const { data: user, isSuccess, isLoading, refetch } = useGetUserQuery(undefined, { skip: !isAuthorized });
    const eventBus = React.useRef(getEventBus()).current;

    const setUserDefaultTab = React.useCallback(
        (tabByDefault: string | undefined) => {
            const defaultTab = getValidTabNameValue(tabByDefault);
            dispatch(handleTabSwitch(defaultTab));
        },
        [dispatch]
    );

    React.useEffect(() => {
        const handleEvent = (value: string | undefined) => {
            if (!sidebarVisible) {
                // this prevents switching the tab in the sidebar
                // when user changing setting in popup while sidebar is open
                setUserDefaultTab(value);
            }

            queueMicrotask(() => {
                refetch();
            });
        };

        eventBus.addListener('onDefaultTabChange', handleEvent);

        return () => {
            eventBus.removeListener('onDefaultTabChange', handleEvent);
        };
    }, [eventBus, refetch, setUserDefaultTab, sidebarVisible]);

    React.useEffect(() => {
        if (!defaultTabInitialized && isSuccess && user) {
            setUserDefaultTab(user.settings.user_default_home_page);

            dispatch(setDefaultTabInitialized(true));

            queueMicrotask(() => {
                refetch();
            });
        }
    }, [isSuccess, user, dispatch, defaultTabInitialized, setUserDefaultTab, refetch]);

    if (enableLoader && (!isUsersFlagsReady || isLoading)) {
        return <PreloadAnimation fullScreen={fullScreenLoader} />;
    }

    return children;
};
